import { injectable } from 'tsyringe';
import { Utils } from '../../utils/utils';
import { Application } from '../../models/application.model';
import { PersistentStorageService } from '../persistent-storage-service/persistent-storage.service';
import { Game } from '../../models/game.model';
import { LoggerService } from '../logger.service';

// -----------------------------------------------------------------------------
// Randomly (weighted) select an application from the a given list and then
// keep state so the next selection won't include a previously selected app
@injectable()
export class WeightedRandomAppSelector {
  private applications: Application[] = [];
  private logger = LoggerService.getCategory('WeightedRandomAppSelector');

  constructor(private readonly persistentStorage: PersistentStorageService) {}

  // ---------------------------------------------------------------------------
  public initialize(game: Game, applications: Application[]) {
    this.applications = Utils.deepClone(applications);

    for (const app of this.applications) {
      const displayedInfo = this.persistentStorage.getDisplayedNotificationInfo(
        game,
        app,
      );

      app.timesDisplayed = displayedInfo?.count ?? 0;
    }
  }

  // ---------------------------------------------------------------------------
  public next(): Application | null {
    if (this.applications?.length === 0) {
      return null;
    }

    try {
      if (this.hasGameEventApps()) {
        const gameEventApp = this.getGameEventApp();
        if (gameEventApp) {
          return gameEventApp;
        }
      }
    } catch (error) {
      this.logger.error('Error selecting game event app', error);
    }

    const shuffledArray = this.fillArrayWithAppIndices();
    this.fisherYatesShuffle(shuffledArray);

    const randomIndex = Utils.randomWholeNumber(0, shuffledArray.length - 1);
    const randomAppIndex = shuffledArray[randomIndex];
    const randomApp = this.applications[randomAppIndex];

    this.applications.splice(randomAppIndex, 1);
    return randomApp;
  }

  // ---------------------------------------------------------------------------
  private hasGameEventApps(): boolean {
    return this.applications.some(
      (app) => !!app?.gameEventTrigger?.triggerEvent,
    );
  }

  // ---------------------------------------------------------------------------
  private getGameEventApp(): Application | undefined {
    const gameEventApps = this.applications
      .map((app, index) => ({ app, index }))
      .filter(({ app }) => !!app?.gameEventTrigger?.triggerEvent);

    if (gameEventApps.length === 0) {
      this.logger.warn('No game event apps found');
      return undefined;
    }

    this.logger.info(`Found ${gameEventApps.length} game event apps`);
    const randomIndex = Utils.randomWholeNumber(0, gameEventApps.length - 1);

    const selectedGameEventApp = gameEventApps[randomIndex];
    if (!selectedGameEventApp) {
      this.logger.warn('No game event app selected');
      return undefined;
    }

    this.logger.warn(
      'selectedGameEventApp',
      selectedGameEventApp?.app?.name ?? 'unknown',
    );

    const appIndex = selectedGameEventApp.index;

    this.logger.warn('appIndex', appIndex);
    const randomApp = this.applications?.[appIndex];
    this.applications.splice(appIndex, 1);
    return randomApp;
  }

  // ---------------------------------------------------------------------------
  private fillArrayWithAppIndices() {
    const appIndicesArray: number[] = [];

    for (let appIndex = 0; appIndex < this.applications.length; ++appIndex) {
      const app = this.applications[appIndex];

      const calculatedWeight =
        app.recommendWeight * (app.displayLimit - (app?.timesDisplayed ?? 0));

      for (let counter = 0; counter < calculatedWeight; ++counter) {
        appIndicesArray.push(appIndex);
      }
    }

    return appIndicesArray;
  }

  // ---------------------------------------------------------------------------
  private fisherYatesShuffle(shuffledArray: number[]) {
    for (let i = shuffledArray.length - 1; i >= 0; --i) {
      const j = Utils.randomWholeNumber(0, i);
      const temp = shuffledArray[i];
      shuffledArray[i] = shuffledArray[j];
      shuffledArray[j] = temp;
    }
  }
}
